import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { App } from '@capacitor/app';

class MobilePaymentService {
  constructor() {
    this.paymentInProgress = false;
    this.currentPaymentData = null;
    this.processingCallback = false;
    // Polling management for ColorOS compatibility
    this.pollInterval = null;
    this.pollingStopped = false;
    // Mobile debugging
    this.debugMode = false;
    this.debugLog = [];
    this.statusCallback = null;
    this.initializeAppListeners();
  }

  // Mobile-friendly debug logging
  log(message, level = 'info') {
    const timestamp = new Date().toISOString();
    const logEntry = { timestamp, message, level };
    this.debugLog.push(logEntry);
    
    // Keep only last 50 entries
    if (this.debugLog.length > 50) {
      this.debugLog = this.debugLog.slice(-50);
    }
    
    // Store in localStorage for retrieval
    localStorage.setItem('mobilePaymentDebugLog', JSON.stringify(this.debugLog));
    
    // Only show UI status updates when debug mode is enabled
    if (this.debugMode && this.statusCallback) {
      this.statusCallback(message, level);
    }
    
    // Also console log for development
    console.log(`[MobilePayment] ${message}`);
  }

  // Set callback for UI status updates
  setStatusCallback(callback) {
    this.statusCallback = callback;
  }

  // Enable debug mode to show logs on screen
  enableDebugMode() {
    this.debugMode = true;
    this.log('Debug mode enabled - logs will be displayed on screen', 'info');
  }

  // Disable debug mode to hide logs from screen
  disableDebugMode() {
    this.debugMode = false;
    console.log('[MobilePayment] Debug mode disabled - logs hidden from screen but still available in console and localStorage');
  }

  // Check if debug mode is enabled
  isDebugModeEnabled() {
    return this.debugMode;
  }

  // Show visual status (can be called from UI)
  showStatus(message, type = 'info') {
    // Only show UI status when debug mode is enabled
    if (this.debugMode) {
      // Try to update UI element if it exists
      const statusElement = document.getElementById('mobile-payment-status');
      if (statusElement) {
        statusElement.textContent = message;
        statusElement.className = `mobile-status ${type}`;
      }
    }
    
    // Always log it (but UI display depends on debug mode)
    this.log(message, type);
  }

  // Initialize app listeners for deep link handling
  initializeAppListeners() {
    if (Capacitor.isNativePlatform()) {
      // Override global User-Agent for mobile app
      this.overrideUserAgent();
      
      // Listen for app URL open events (deep links)
      App.addListener('appUrlOpen', (data) => {
        console.log('App opened with URL:', data.url);
        this.handleDeepLink(data.url);
      });

      // Listen for app state changes
      App.addListener('appStateChange', (state) => {
        console.log('App state changed:', state);
        if (state.isActive && this.paymentInProgress) {
          // App became active, check if we need to handle payment callback
          this.checkPendingPaymentCallback();
        }
      });
    }
  }

  // Clean up backup authentication tokens after payment completion
  async cleanupBackupTokens() {
    try {
      localStorage.removeItem('paymentBackupToken');
      sessionStorage.removeItem('idToken');
      sessionStorage.removeItem('accessToken');
      
      // Clean up Capacitor secure storage if available
      if (Capacitor.isNativePlatform()) {
        try {
          const { Preferences } = await import('@capacitor/preferences');
          await Preferences.remove({ key: 'authToken' });
          this.log('Backup tokens cleaned from secure storage', 'info');
        } catch (secureStorageError) {
          // Silently handle cleanup error
        }
      }
      this.log('Backup authentication tokens cleaned up', 'info');
    } catch (error) {
      // Silently handle cleanup errors
    }
  }

  // Override user agent for mobile app
  overrideUserAgent() {
    if (Capacitor.isNativePlatform()) {
      // Set custom user agent for CCAvenue mobile detection
      try {
        const originalUserAgent = navigator.userAgent;
        const mobileUA = originalUserAgent.includes('wv') ? 
          originalUserAgent : 
          originalUserAgent + ' wv';
        
        Object.defineProperty(navigator, 'userAgent', {
          get: () => mobileUA,
          configurable: true
        });
      } catch (error) {
        // Silently handle user agent override failure
      }
    }
  }

  // Check if we're running in a mobile app
  isMobileApp() {
    return Capacitor.isNativePlatform();
  }

  // Get mobile app user agent string
  getMobileUserAgent() {
    const platform = Capacitor.getPlatform();
    return `MyHomeEntryApp/1.0 (${platform}; wv) Capacitor`;
  }

  // Set custom headers for mobile app identification
  getMobileHeaders() {
    return {
      'User-Agent': this.getMobileUserAgent(),
      'X-App-Platform': 'capacitor',
      'X-App-Version': '1.0'
    };
  }

  // Handle payment initiation for mobile app using CCAvenue WebView approach
  async initiateMobilePayment(paymentData) {
    try {
      // Prevent duplicate payment submissions
      if (this.paymentInProgress) {
        console.log('Payment already in progress, ignoring duplicate request');
        throw new Error('Payment already in progress. Please wait for the current payment to complete.');
      }

      this.paymentInProgress = true;
      this.currentPaymentData = paymentData;

      this.log('Initiating mobile payment', 'info');

      // Store payment data in local storage for callback handling
      
      localStorage.setItem('pendingPayment', JSON.stringify(paymentData));
      localStorage.setItem('paymentStartTime', Date.now().toString());
      
      // Store backup authentication tokens for mobile WebView recovery
      const currentIdToken = localStorage.getItem('idToken');
      const currentAccessToken = localStorage.getItem('accessToken');
      
      if (currentIdToken || currentAccessToken) {
        // Store in multiple locations for redundancy
        const backupToken = currentIdToken || currentAccessToken;
        localStorage.setItem('paymentBackupToken', backupToken);
        sessionStorage.setItem('idToken', backupToken);
        sessionStorage.setItem('accessToken', backupToken);
        
        // Store in Capacitor secure storage if available
        if (Capacitor.isNativePlatform()) {
          try {
            const { Preferences } = await import('@capacitor/preferences');
            await Preferences.set({ key: 'authToken', value: backupToken });
            this.log('Authentication token backed up to secure storage', 'info');
          } catch (secureStorageError) {
            this.log('Failed to backup token to secure storage', 'warning');
          }
        }
        
        this.log('Authentication tokens backed up for payment recovery', 'info');
      } else {
        this.log('WARNING: No authentication tokens found to backup', 'warning');
      }
      
      this.log('Payment data and backup tokens stored successfully', 'info');
      
     // According to CCAvenue mobile integration guide:
      // Open CCAvenue payment page in WebView and monitor for redirect/cancel URL
      const ccavenueUrl = this.buildCCAvenueURL(paymentData);
      
      console.log('Opening CCAvenue WebView with URL:', ccavenueUrl);
      
      // Use Browser plugin to open CCAvenue in WebView
      await Browser.open({
        url: ccavenueUrl,
        windowName: '_self',
        toolbarColor: '#2196F3',
        presentationStyle: 'fullscreen',
        showTitle: false // Hide title bar for better UX
      });

      // Listen for URL changes to detect payment completion
      // The WebView will redirect to our callback URL when payment is done
      this.setupWebViewListener();

      // Start payment status monitoring
      this.showStatus('Monitoring payment status...', 'info');
      
      // Validate paymentId before starting polling
      if (!paymentData.paymentId) {
        throw new Error('PaymentId is required for polling');
      }
      
      this.startPaymentPolling(paymentData.paymentId);

      return { success: true };
    } catch (error) {
      console.error('Mobile payment initiation error:', error);
      this.paymentInProgress = false;
      this.currentPaymentData = null;
      localStorage.removeItem('pendingPayment');
      localStorage.removeItem('paymentStartTime');
      
      // Clean up backup tokens on error
      this.cleanupBackupTokens();
      
      throw error;
    }
  }

  // Build CCAvenue URL according to official mobile integration guide
  buildCCAvenueURL(paymentData) {
    // CCAvenue mobile integration guide specifies these exact parameter names:
    // enc_val (or encRequest) and access_code (or accessCode)
    const encVal = paymentData.enc_val || paymentData.encRequest;
    const accessCode = paymentData.access_code || paymentData.accessCode;
    
    if (!encVal || !accessCode) {
      throw new Error('Missing required CCAvenue parameters: enc_val and access_code');
    }
    
    console.log('Building CCAvenue URL with enc_val:', encVal?.substring(0, 50) + '...', 'access_code:', accessCode);
    
    // According to CCAvenue mobile guide, use this exact URL format:
    // https://test.ccavenue.com/transaction.do?command=initiateTransaction&encRequest=enc_val&access_code=access_code
    const params = new URLSearchParams({
      command: 'initiateTransaction',
      encRequest: encVal,
      access_code: accessCode,
      // Add mobile app indicator for better detection in callback
      mobile_app: '1'
    });

    // Use test CCAvenue endpoint for development
    //const baseUrl = 'https://test.ccavenue.com/transaction.do';
    const baseUrl = 'https://secure.ccavenue.com/transaction.do';
    const finalUrl = `${baseUrl}?${params.toString()}`;
    
    console.log('Final CCAvenue URL with mobile indicator:', finalUrl);
    return finalUrl;
  }

  // Setup WebView listener to monitor for payment completion
  setupWebViewListener() {
    if (Capacitor.isNativePlatform()) {
      // According to CCAvenue guide: Monitor for redirect/cancel URL in WebView
      // When payment completes, CCAvenue redirects to our callback URL
      console.log('WebView listener ready - will detect payment completion via URL redirect');
      
      // Listen for Browser finish event (when WebView is closed or redirected)
      Browser.addListener('browserFinished', () => {
        console.log('Browser finished - WebView closed');
        this.handleWebViewClosed();
      });
      
      // Listen for page load to detect custom scheme redirects
      Browser.addListener('browserPageLoaded', () => {
        console.log('Browser page loaded');
      });
    }
  }

  // Handle WebView closure
  handleWebViewClosed() {
    console.log('WebView closed, but continuing payment polling...');
    
    // 🚨 CRITICAL FIX: DO NOT stop polling when WebView closes!
    // CCAvenue redirecting to web callback is NORMAL - polling should continue
    // to detect when payment status updates in the database
    
    this.log('WebView closed - CCAvenue redirected to web callback (this is normal)', 'info');
    this.log('Continuing to poll for payment status updates...', 'info');
    
    // DO NOT call this.stopPaymentPolling() here!
    // Let polling continue until it detects the payment completion
    
    return; // Just return, let polling handle status detection
  }

  // Start payment status polling
  startPaymentPolling(paymentId) {
    this.log(`Starting payment polling for: ${paymentId}`, 'info');
    this.showStatus('Starting payment monitoring...', 'info');
    this.pollingStopped = false;
    
    // Validate paymentId before starting polling
    if (!paymentId || paymentId === 'undefined' || paymentId === 'null' || typeof paymentId !== 'string' || paymentId.trim() === '') {
      throw new Error('Invalid paymentId provided for polling');
    }
    
    // Clean and validate paymentId
    const cleanPaymentId = paymentId.trim();
    if (cleanPaymentId.length === 0) {
      throw new Error('PaymentId is empty after trimming');
    }
    
    let pollCount = 0;
    const poll = async () => {
      pollCount++;
      
      // CRITICAL DEBUG: Log the cleanPaymentId being used
      this.log(`🔍 Poll attempt #${pollCount} - cleanPaymentId: "${cleanPaymentId}" (type: ${typeof cleanPaymentId}, length: ${cleanPaymentId ? cleanPaymentId.length : 0})`, 'info');
      
      // Skip if payment completed via callback or polling was manually stopped
      if (this.pollingStopped || this.processingCallback || !this.paymentInProgress) {
        this.log('Polling stopped - payment completed or cancelled', 'info');
        return;
      }

      // Additional validation at runtime
      if (!cleanPaymentId || cleanPaymentId === 'undefined' || cleanPaymentId === 'null') {
        this.log('PaymentId invalid during poll, stopping', 'error');
        this.stopPaymentPolling();
        return;
      }

      try {
        this.showStatus(`Checking payment status (attempt ${pollCount})...`, 'info');
        
        const result = await this.checkPaymentStatus(cleanPaymentId);
        
        if (result && result.status !== 'PENDING') {
          this.log(`Payment completed: ${result.status}`, 'info');
          this.showStatus('Payment completed! Redirecting...', 'success');
          this.stopPaymentPolling();
          this.handlePollingSuccess(result);
        } else {
          this.showStatus(`Verifying payment... (${pollCount})`, 'info');
        }
      } catch (error) {
        this.log(`Polling error: ${error.message}`, 'error');
        // Don't show errors to user, keep checking silently
      }
    };

    // Poll every 3 seconds
    this.pollInterval = setInterval(poll, 3000);
    
    // Safety timeout after 5 minutes
    setTimeout(() => {
      if (this.pollInterval) {
        this.log('Polling timeout reached', 'warning');
        this.showStatus('Payment verification timed out', 'error');
        this.stopPaymentPolling();
        this.handlePaymentTimeout();
      }
    }, 300000);
    
    // Do immediate first poll after 1 second
    setTimeout(poll, 1000);
  }

  // Stop payment status polling
  stopPaymentPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
    this.pollingStopped = true;
  }

  // Check payment status API call
  async checkPaymentStatus(paymentId) {
    // Validate paymentId before proceeding 
    if (!paymentId || paymentId === 'undefined' || paymentId === 'null' || typeof paymentId !== 'string' || paymentId.trim() === '') {
      throw new Error('Invalid paymentId provided');
    }
    
    // Clean paymentId
    const cleanPaymentId = paymentId.trim();
    if (cleanPaymentId.length === 0) {
      throw new Error('PaymentId is empty');
    }
    
    const apiUrl = process.env.REACT_APP_API_URL || 'https://myhomeentry.com/api';
    
    // Enhanced authentication token recovery for mobile payments
    let token = localStorage.getItem('idToken') || localStorage.getItem('accessToken');

    if (!token) {
      // Recovery attempt 1: Check backup token storage
      const backupToken = localStorage.getItem('paymentBackupToken') || 
                        sessionStorage.getItem('idToken') || 
                        sessionStorage.getItem('accessToken');
      
      if (backupToken) {
        token = backupToken;
        localStorage.setItem('idToken', backupToken);
      } else {
        // Recovery attempt 2: Check stored user session data
        const storedUserSession = localStorage.getItem('userSession') || localStorage.getItem('userData');
        if (storedUserSession) {
          try {
            const userData = JSON.parse(storedUserSession);
            if (userData.token || userData.idToken || userData.accessToken) {
              token = userData.token || userData.idToken || userData.accessToken;
              localStorage.setItem('idToken', token);
            }
          } catch (e) {
            // Silent fail for parsing errors
          }
        }
      }
      
      // Recovery attempt 3: Try Capacitor secure storage
      if (!token && Capacitor.isNativePlatform()) {
        try {
          const { Preferences } = await import('@capacitor/preferences');
          const { value: secureToken } = await Preferences.get({ key: 'authToken' });
          if (secureToken) {
            token = secureToken;
            localStorage.setItem('idToken', token);
          }
        } catch (secureStorageError) {
          // Silent fail for secure storage errors
        }
      }
    }

    if (!token) {
      throw new Error('No authentication token available');
    }

    // Construct URL with validated paymentId
    const fullApiUrl = `${apiUrl}/payments/${encodeURIComponent(cleanPaymentId)}`;

    const response = await fetch(fullApiUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Payment API returned ${response.status}: ${errorText}`);
    }

    const paymentRecord = await response.json();
    this.log(`Payment record status: ${paymentRecord.status}`, 'info');
    
    // Map DynamoDB status to CCAvenue format
    let ccaStatus, statusMessage;
    if (paymentRecord.status === 'COMPLETED') {
      ccaStatus = 'Success';
      statusMessage = paymentRecord.ccavenueStatusMessage || 'Payment completed successfully';
    } else if (paymentRecord.status === 'CANCELLED') {
      ccaStatus = 'Aborted';
      statusMessage = paymentRecord.ccavenueStatusMessage || 'Payment was cancelled';
    } else if (paymentRecord.status === 'FAILED') {
      ccaStatus = 'Failure';
      statusMessage = paymentRecord.ccavenueStatusMessage || 'Payment failed';
    } else {
      return { status: 'PENDING' }; // Continue polling
    }

    const result = {
      status: ccaStatus,
      orderId: paymentRecord.ccavenueOrderId || '',
      transactionId: paymentRecord.ccavenueTrackingId || '',
      amount: paymentRecord.amount ? paymentRecord.amount.toString() : '',
      message: statusMessage,
      paymentId: paymentRecord.paymentId,
      flatId: paymentRecord.flatId,
      buildingId: paymentRecord.buildingId
    };
    
    return result;
  }

  // Handle successful polling result
  handlePollingSuccess(result) {
    this.showStatus('Payment successful! Loading results...', 'success');
    
    // Prevent WebView closer from also processing
    this.processingCallback = true;
    
    // Close WebView forcefully
    if (Capacitor.isNativePlatform()) {
      try {
        Browser.close();
      } catch (browserError) {
        // Silent fail for browser close errors
      }
    }
    
    // Store result and navigate (same as existing logic)
    localStorage.setItem('mobilePaymentResult', JSON.stringify(result));
    
    // Clean up backup tokens after successful payment
    this.cleanupBackupTokens();
    
    this.paymentInProgress = false;
    this.currentPaymentData = null;
    localStorage.removeItem('pendingPayment');
    localStorage.removeItem('paymentStartTime');
    
    // Check if we're already on payment-callback page
    if (window.location.pathname === '/payment-callback') {
      this.log('Already on payment-callback page - reloading', 'info');
      window.location.reload();
    } else {
      this.log('Navigating to payment-callback page', 'info');
      window.location.href = '/payment-callback';
    }
  }

  // Handle polling timeout
  handlePaymentTimeout() {
    console.log('Payment polling timeout - redirecting to callback');
    
    if (Capacitor.isNativePlatform()) {
      Browser.close();
    }
    
    this.paymentInProgress = false;
    this.currentPaymentData = null;
    localStorage.removeItem('pendingPayment');
    localStorage.removeItem('paymentStartTime');
    
    window.location.href = '/payment-callback';
  }

  // Handle deep link callback from CCAvenue
  // Handle deep link callback from CCAvenue
  handleDeepLink(url) {
    try {
      const urlObj = new URL(url);
      
      // Check if this is a payment callback
      if (urlObj.protocol === 'myhomeentry:' && (urlObj.pathname === '//payment-callback' || urlObj.pathname === '/payment-callback' || url.includes('payment-callback'))) {
        this.log('Deep link payment callback received', 'success');
        
        // Stop polling since deep link worked
        this.stopPaymentPolling();
        
        this.processingCallback = true;
        
        // Delay WebView closure to ensure deep link is fully processed
        setTimeout(() => {
          if (Capacitor.isNativePlatform()) {
            Browser.close();
          }
        }, 500);
        
        // Immediate UI hiding to prevent flash
        document.body.style.visibility = 'hidden';
        
        const params = new URLSearchParams(urlObj.search);
        this.processPaymentCallback(params);
      }
    } catch (error) {
      this.processingCallback = false;
    }
  }

  // Process payment callback from deep link
  async processPaymentCallback(params) {
    try {
      const paymentResult = {
        status: params.get('status'),
        orderId: params.get('orderId'),
        transactionId: params.get('transactionId'),
        amount: params.get('amount'),
        message: params.get('message'),
        paymentId: params.get('paymentId'),
        flatId: params.get('flatId'),
        buildingId: params.get('buildingId'),
        error: params.get('error')
      };

      // Store payment result in localStorage for PaymentCallback component
      localStorage.setItem('mobilePaymentResult', JSON.stringify(paymentResult));
      
      // Clear payment in progress flags
      this.paymentInProgress = false;
      this.currentPaymentData = null;
      this.processingCallback = false;
      localStorage.removeItem('pendingPayment');
      localStorage.removeItem('paymentStartTime');
      
      // Build URL with all payment parameters
      const urlParams = new URLSearchParams();
      urlParams.set('status', paymentResult.status || 'Unknown');
      urlParams.set('orderId', paymentResult.orderId || '');
      urlParams.set('paymentId', paymentResult.paymentId || '');
      urlParams.set('flatId', paymentResult.flatId || '');
      urlParams.set('buildingId', paymentResult.buildingId || '');
      urlParams.set('transactionId', paymentResult.transactionId || '');
      urlParams.set('amount', paymentResult.amount || '');
      urlParams.set('message', paymentResult.message || ((paymentResult.status === 'Success' || paymentResult.status === 'Shipped') ? 'Payment completed successfully' : 'Payment failed'));
      urlParams.set('mobile', 'true');
      
      // Add error parameter for failed payments
      if (paymentResult.status !== 'Success' && paymentResult.status !== 'Shipped' && paymentResult.error) {
        urlParams.set('error', paymentResult.error);
      }
      
      // Navigate to payment callback
      const callbackUrl = `/payment-callback?${urlParams.toString()}`;
      window.location.href = callbackUrl;

    } catch (error) {
      this.processingCallback = false;
      // Store error result and navigate to error page
      const errorResult = {
        status: 'Failed',
        error: error.message,
        message: 'Payment processing error'
      };
      localStorage.setItem('mobilePaymentResult', JSON.stringify(errorResult));
      
      // Navigate to payment callback with error
      window.location.href = `/payment-callback?error=${encodeURIComponent(error.message)}&mobile=true`;
    }
  }

  // Check for pending payment callback (when app becomes active)
  checkPendingPaymentCallback() {
    const pendingPayment = localStorage.getItem('pendingPayment');
    const paymentStartTime = localStorage.getItem('paymentStartTime');
    
    if (pendingPayment && paymentStartTime) {
      const startTime = parseInt(paymentStartTime);
      const currentTime = Date.now();
      const elapsedTime = currentTime - startTime;
      
      // If more than 5 minutes have passed, assume payment was abandoned
      if (elapsedTime > 5 * 60 * 1000) {
        this.paymentInProgress = false;
        this.currentPaymentData = null;
        localStorage.removeItem('pendingPayment');
        localStorage.removeItem('paymentStartTime');
      }
    }
  }

  // Check if a payment is currently in progress
  isPaymentInProgress() {
    return this.paymentInProgress;
  }

  // Get current payment data
  getCurrentPaymentData() {
    return this.currentPaymentData;
  }

  // Reset payment state (prevents infinite loops)
  resetPaymentState() {
    console.log('Resetting payment state to prevent infinite loops');
    
    // Stop polling
    this.stopPaymentPolling();
    
    this.paymentInProgress = false;
    this.currentPaymentData = null;
    this.processingCallback = false;
    localStorage.removeItem('pendingPayment');
    localStorage.removeItem('paymentStartTime');
    localStorage.removeItem('mobilePaymentResult');
  }

  // Cancel current payment
  cancelPayment() {
    // Stop polling
    this.stopPaymentPolling();
    
    this.paymentInProgress = false;
    this.currentPaymentData = null;
    this.processingCallback = false;
    localStorage.removeItem('pendingPayment');
    localStorage.removeItem('paymentStartTime');
    
    // Clean up backup tokens when payment is cancelled
    this.cleanupBackupTokens();
    
    // Close browser if open
    if (Capacitor.isNativePlatform()) {
      Browser.close();
    }
  }

  // Check if a payment is currently in progress
  isPaymentInProgress() {
    return this.paymentInProgress;
  }

  // Get current payment data
  getCurrentPaymentData() {
    return this.currentPaymentData;
  }
}

// Export singleton instance for use in other components
export const mobilePaymentService = new MobilePaymentService();

// Make it globally accessible for debugging
if (typeof window !== 'undefined') {
  window.mobilePaymentService = mobilePaymentService;
}